import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import dayjs from "dayjs";
import {
    Button, Card, Col, DatePicker, Divider, Form, Input, Row, Select, Steps, Switch,
    Typography, Upload, Alert, Tag,
} from "antd";
import {
    BankOutlined, UserOutlined, SafetyCertificateOutlined, FileProtectOutlined,
    UploadOutlined, ArrowLeftOutlined, ArrowRightOutlined, CheckCircleOutlined,
} from "@ant-design/icons";
import { API_URL } from "../../variables/constants";
import axiosInstance from "../../variables/api/axiosInstance";
import axiosMultiPartDataInstance from "../../variables/api/axiosMultiPartDatainstance";
import entete from "../../assets/img/entete_pool.png";

const { Title, Text, Paragraph } = Typography;
const PRIMARY = "#242166";

// ===== Utils =====
const onlyDigits = (s = "") => `${s ?? ""}`.replace(/\D/g, "");
const formatTgPhone = (s = "") => onlyDigits(s).slice(0, 8).replace(/(\d{2})(?=\d)/g, "$1 ").trim();
const formatCNI = (s = "") => {
    const d = onlyDigits(s).slice(0, 11);
    return [d.slice(0, 4), d.slice(4, 7), d.slice(7, 11)].filter(Boolean).join("-");
};
const TG_PHONE = /^(22|90|91|92|93|96|98|99|70|71|79)\d{6}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Règles réutilisables
const phoneRule = (required = true) => ({
    validator: (_, v) => {
        const d = onlyDigits(v);
        if (!d) return required ? Promise.reject(new Error("Le numéro est requis")) : Promise.resolve();
        return TG_PHONE.test(d) ? Promise.resolve() : Promise.reject(new Error("Numéro invalide (8 chiffres)"));
    },
});
const pieceRule = (form, typeField) => ({
    validator: (_, v) => {
        const type = form.getFieldValue(typeField);
        const d = onlyDigits(v);
        if (!v) return Promise.reject(new Error("La pièce d'identité est requise"));
        if (type === "CNI") return d.length === 11 ? Promise.resolve() : Promise.reject(new Error("Format CNI : 0000-000-0000"));
        return /^[A-Za-z]{1,2}\d{5,}$/.test(v) ? Promise.resolve() : Promise.reject(new Error("Format de passeport invalide"));
    },
});

const RegisterV2 = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [insurances, setInsurances] = useState([]);
    const [villes, setVilles] = useState([]);
    const [current, setCurrent] = useState(0);
    const [responsableEstAdmin, setResponsableEstAdmin] = useState(true);
    // agreements : { [code]: { company, file } }
    const [agreements, setAgreements] = useState({});
    const [selectedCodes, setSelectedCodes] = useState([]);

    useEffect(() => {
        axios.get(`${API_URL}/api/organisme/all/assurances-actives`)
            .then((res) => setInsurances(Array.isArray(res.data) ? res.data : res.data?.insurances ?? []))
            .catch((err) => console.error(err));
        axiosInstance.get(`/api/parametre/villes`)
            .then((res) => setVilles(res.data || []))
            .catch((err) => console.log(err));
    }, []);

    // ===== Étapes (dynamiques selon le switch responsable=admin) =====
    const steps = useMemo(() => {
        const arr = [
            {
                key: "societe", title: "Société", icon: <BankOutlined />,
                fields: ["raisonSociale", "siegeSocial", "ville", "email", "nif", "telephone1", "telephone2"],
            },
            {
                key: "responsable", title: "Responsable", icon: <UserOutlined />,
                fields: ["nomResponsable", "prenomResponsable", "genreResponsable", "emailResponsable",
                    "telephoneResponsable", "adresseResponsable", "typePieceResponsable", "numIdentiteResponsable",
                    "dateNaissanceResponsable", "dateDelivranceResponsable", "lieuDelivranceResponsable"],
            },
        ];
        if (!responsableEstAdmin) {
            arr.push({
                key: "admin", title: "Administrateur", icon: <SafetyCertificateOutlined />,
                fields: ["nomAdmin", "prenomAdmin", "genreAdmin", "emailAdmin", "telephoneAdmin",
                    "adresseAdmin", "typePieceAdmin", "numIdentiteAdmin", "dateDelivranceAdmin", "lieuDelivranceAdmin"],
            });
        }
        arr.push({ key: "agrements", title: "Agréments", icon: <FileProtectOutlined />, fields: [] });
        return arr;
    }, [responsableEstAdmin]);

    // Si on repasse en mode "responsable = admin" alors qu'on était sur l'étape admin/agréments.
    useEffect(() => {
        if (current > steps.length - 1) setCurrent(steps.length - 1);
    }, [steps.length, current]);

    const next = async () => {
        try {
            const fields = steps[current].fields;
            if (fields.length) await form.validateFields(fields);
            setCurrent((c) => Math.min(c + 1, steps.length - 1));
        } catch (e) {
            toast.warning("Veuillez corriger les champs en rouge.");
        }
    };
    const prev = () => setCurrent((c) => Math.max(c - 1, 0));

    // ===== Agréments =====
    const handleSelectCompagnies = (codes) => {
        setSelectedCodes(codes);
        setAgreements((prevAgr) => {
            const map = {};
            codes.forEach((code) => {
                const company = insurances.find((i) => i.code === code);
                map[code] = prevAgr[code] || { company, file: null };
            });
            return map;
        });
    };
    const attachDoc = (code, file) => {
        setAgreements((prevAgr) => ({ ...prevAgr, [code]: { ...prevAgr[code], file } }));
        return false; // empêche l'upload auto
    };

    // ===== Soumission =====
    const handleSubmit = async () => {
        // Valide tous les champs (toutes étapes) avant envoi.
        try {
            const allFields = steps.flatMap((s) => s.fields);
            await form.validateFields(allFields);
        } catch (e) {
            toast.warning("Des informations sont manquantes ou invalides.");
            return;
        }
        if (!selectedCodes.length) {
            toast.warning("Sélectionnez les compagnies avec lesquelles vous avez des agréments.");
            return;
        }
        const missing = selectedCodes.some((code) => !agreements[code]?.file);
        if (missing) {
            toast.warning("Veuillez joindre le document d'agrément de chaque compagnie.");
            return;
        }

        const v = form.getFieldsValue(true);
        setLoading(true);
        try {
            // 1) Upload des agréments
            const data = new FormData();
            selectedCodes.forEach((code) => {
                const { company, file } = agreements[code];
                const ext = file?.name?.substring(file.name.lastIndexOf(".") + 1) || "pdf";
                const filename = `Agreement_${company.code}_${(v.raisonSociale || "").split(" ").join("_")}.${ext}`;
                data.append("fichier", file, filename);
                data.append("compagnies", company.code);
            });
            const upload = await axiosMultiPartDataInstance.post(`/api/agreement/all`, data);

            // 2) Construction du payload courtier
            const courtier = {
                raisonSociale: v.raisonSociale,
                siegeSocial: v.siegeSocial,
                ville: v.ville,
                email: v.email,
                nif: v.nif,
                nomResponsable: v.nomResponsable,
                prenomResponsable: v.prenomResponsable,
                emailResponsable: v.emailResponsable,
                genreResponsable: v.genreResponsable,
                adresseResponsable: v.adresseResponsable,
                telephoneResponsable: [onlyDigits(v.telephoneResponsable)],
                typePieceResponsable: v.typePieceResponsable,
                numIdentiteResponsable: onlyDigits(v.numIdentiteResponsable),
                dateNaissanceResponsable: v.dateNaissanceResponsable ? dayjs(v.dateNaissanceResponsable).format("YYYY-MM-DD") : null,
                dateDelivranceResponsable: v.dateDelivranceResponsable ? dayjs(v.dateDelivranceResponsable).format("YYYY-MM-DD") : null,
                lieuDelivranceResponsable: v.lieuDelivranceResponsable,
                contact: v.telephone2
                    ? [onlyDigits(v.telephone1), onlyDigits(v.telephone2)]
                    : [onlyDigits(v.telephone1)],
                agreements: upload.data,
            };

            // Administrateur : par défaut le responsable EST l'administrateur → on n'envoie
            // pas d'admin distinct (le backend ne crée alors qu'un seul utilisateur, basé sur
            // le responsable). Sinon on renseigne le 2e administrateur.
            if (!responsableEstAdmin) {
                Object.assign(courtier, {
                    nomAdmin: v.nomAdmin,
                    prenomAdmin: v.prenomAdmin,
                    emailAdmin: v.emailAdmin,
                    genreAdmin: v.genreAdmin,
                    adresseAdmin: v.adresseAdmin,
                    telephoneAdmin: [onlyDigits(v.telephoneAdmin)],
                    typePieceAdmin: v.typePieceAdmin,
                    numIdentiteAdmin: onlyDigits(v.numIdentiteAdmin),
                    dateDelivranceAdmin: v.dateDelivranceAdmin ? dayjs(v.dateDelivranceAdmin).format("YYYY-MM-DD") : null,
                    lieuDelivranceAdmin: v.lieuDelivranceAdmin,
                });
            }

            await axiosInstance.post(`/api/agence/courtier`, JSON.stringify(courtier));
            toast.success("Enregistrement effectué avec succès !");
            navigate("/waiting");
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors de l'enregistrement.");
        } finally {
            setLoading(false);
        }
    };

    // ===== Rendu des champs =====
    const villeOptions = villes.map((vil) => ({ value: vil.libelleParametre, label: vil.libelleParametre }));
    const genreOptions = [{ value: "MASCULIN", label: "Masculin" }, { value: "FEMININ", label: "Féminin" }];
    const pieceOptions = [{ value: "CNI", label: "CNI" }, { value: "PASSEPORT", label: "Passeport" }];

    const PersonFields = ({ prefix }) => {
        // Nom de champ = suffixe + Préfixe capitalisé (ex. "nom" + "Responsable" = "nomResponsable").
        // DOIT correspondre aux clés du payload (v.nomResponsable…) ET à step.fields, sinon les
        // valeurs saisies sont perdues (nom/prénom envoyés null à l'inscription du courtier).
        const F = (suffix) => `${suffix}${prefix.charAt(0).toUpperCase()}${prefix.slice(1)}`;
        return (
            <Row gutter={16}>
                <Col xs={24} md={8}>
                    <Form.Item name={F("nom")} label="Nom" rules={[{ required: true, min: 2, message: "Nom requis" }]}>
                        <Input placeholder="Nom" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item name={F("prenom")} label="Prénom(s)" rules={[{ required: true, min: 2, message: "Prénom requis" }]}>
                        <Input placeholder="Prénom(s)" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item name={F("genre")} label="Genre" rules={[{ required: true }]}>
                        <Select options={genreOptions} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item name={F("email")} label="Adresse e-mail"
                        rules={[{ required: true, message: "E-mail requis" }, { pattern: EMAIL, message: "E-mail invalide" }]}>
                        <Input placeholder="exemple@domaine.com" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item name={F("telephone")} label="Téléphone"
                        getValueFromEvent={(e) => formatTgPhone(e.target.value)} rules={[phoneRule(true)]}>
                        <Input placeholder="00 00 00 00" />
                    </Form.Item>
                </Col>
                <Col xs={24}>
                    <Form.Item name={F("adresse")} label="Adresse">
                        <Input placeholder="Adresse" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item name={F("typePiece")} label="Type de pièce" rules={[{ required: true }]}>
                        <Select options={pieceOptions} onChange={() => form.validateFields([F("numIdentite")])} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item name={F("numIdentite")} label="N° de la pièce"
                        getValueFromEvent={(e) => (form.getFieldValue(F("typePiece")) === "CNI" ? formatCNI(e.target.value) : e.target.value)}
                        rules={[pieceRule(form, F("typePiece"))]}>
                        <Input placeholder="0000-000-0000" />
                    </Form.Item>
                </Col>
                {prefix === "responsable" && (
                    <Col xs={24} md={8}>
                        <Form.Item name="dateNaissanceResponsable" label="Date de naissance"
                            rules={[{ required: true, message: "Date de naissance requise" }, {
                                validator: (_, d) => {
                                    if (!d) return Promise.resolve();
                                    const age = dayjs().diff(d, "year");
                                    if (d.isAfter(dayjs())) return Promise.reject(new Error("Date dans le futur"));
                                    if (age < 18) return Promise.reject(new Error("Âge minimum : 18 ans"));
                                    if (age > 120) return Promise.reject(new Error("Âge invalide"));
                                    return Promise.resolve();
                                },
                            }]}>
                            <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" disabledDate={(d) => d && d.isAfter(dayjs())} />
                        </Form.Item>
                    </Col>
                )}
                <Col xs={24} md={prefix === "responsable" ? 12 : 12}>
                    <Form.Item name={F("dateDelivrance")} label="Date de délivrance"
                        rules={[{ required: true, message: "Date de délivrance requise" },
                        { validator: (_, d) => (!d || !d.isAfter(dayjs())) ? Promise.resolve() : Promise.reject(new Error("Date dans le futur")) }]}>
                        <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" disabledDate={(d) => d && d.isAfter(dayjs())} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item name={F("lieuDelivrance")} label="Lieu de délivrance" rules={[{ required: true, message: "Lieu requis" }]}>
                        <Input placeholder="Lieu de délivrance" />
                    </Form.Item>
                </Col>
            </Row>
        );
    };

    const stepKey = steps[current]?.key;

    return (
        <div style={{
            minHeight: "100vh", position: "relative", padding: "32px 16px",
            // Chemin ABSOLU (racine) : à la route /inscription/v2, un chemin relatif
            // "assets/..." se résolvait sur /inscription/assets/... → 404 → fond blanc.
            backgroundImage: `url(${process.env.PUBLIC_URL}/assets/img/landing.png)`,
            backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat",
            backgroundColor: "#242166",
        }}>
            {/* Voile léger pour garder le texte blanc lisible, sans masquer l'image. */}
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(36,33,102,0.45) 0%, rgba(36,33,102,0.20) 35%, rgba(36,33,102,0.30) 100%)", zIndex: 0 }} />
            <ToastContainer position="top-center" />
            <div style={{ maxWidth: 1000, margin: "0 auto", position: "relative", zIndex: 1 }}>
                {/* En-tête sobre */}
                <div style={{ textAlign: "center", marginBottom: 24 }}>
                    <div style={{ display: "inline-block", background: "rgba(255,255,255,0.95)", borderRadius: 16, padding: "14px 28px", boxShadow: "0 8px 24px rgba(0,0,0,0.18)" }}>
                        <img src={entete} alt="Pool TPVM-VT GIE" style={{ height: 60 }} />
                    </div>
                    <Title level={3} style={{ margin: "16px 0 0", color: "#fff", textShadow: "0 2px 8px rgba(0,0,0,0.35)" }}>Inscription — Société de courtage</Title>
                    <Text style={{ color: "rgba(255,255,255,0.92)", textShadow: "0 1px 4px rgba(0,0,0,0.3)" }}>Renseignez les informations ci-dessous pour soumettre votre demande d'adhésion.</Text>
                </div>

                <Card style={{ borderRadius: 14, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }} bodyStyle={{ padding: "28px 28px 20px" }}>
                    <Steps
                        current={current}
                        items={steps.map((s) => ({ title: s.title, icon: s.icon }))}
                        style={{ marginBottom: 28 }}
                    />

                    <Form form={form} layout="vertical" requiredMark="optional"
                        initialValues={{ genreResponsable: "MASCULIN", typePieceResponsable: "CNI", genreAdmin: "MASCULIN", typePieceAdmin: "CNI" }}>

                        {/* ÉTAPE SOCIÉTÉ */}
                        <div style={{ display: stepKey === "societe" ? "block" : "none" }}>
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item name="raisonSociale" label="Dénomination sociale" rules={[{ required: true, min: 3, message: "Dénomination requise" }]}>
                                        <Input placeholder="Nom de la société" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item name="email" label="E-mail de la société"
                                        rules={[{ required: true, message: "E-mail requis" }, { pattern: EMAIL, message: "E-mail invalide" }]}>
                                        <Input placeholder="contact@societe.com" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item name="siegeSocial" label="Siège social" rules={[{ required: true, min: 3, message: "Siège social requis" }]}>
                                        <Input placeholder="Adresse du siège" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item name="ville" label="Ville">
                                        <Select showSearch optionFilterProp="label" placeholder="Sélectionnez la ville" options={villeOptions} allowClear />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item name="nif" label="NIF" rules={[{ required: true, message: "NIF requis" }, { pattern: /^\d{10,}$/, message: "NIF invalide (≥ 10 chiffres)" }]}>
                                        <Input placeholder="NIF" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item name="telephone1" label="Téléphone principal"
                                        getValueFromEvent={(e) => formatTgPhone(e.target.value)} rules={[phoneRule(true)]}>
                                        <Input placeholder="00 00 00 00" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Form.Item name="telephone2" label="Téléphone secondaire (optionnel)"
                                        getValueFromEvent={(e) => formatTgPhone(e.target.value)} rules={[phoneRule(false)]}>
                                        <Input placeholder="00 00 00 00" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>

                        {/* ÉTAPE RESPONSABLE */}
                        <div style={{ display: stepKey === "responsable" ? "block" : "none" }}>
                            <Alert
                                type="info" showIcon style={{ marginBottom: 20, borderRadius: 8 }}
                                message={
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                                        <span>Le responsable est l'<strong>administrateur</strong> de la plateforme par défaut.</span>
                                        <span>
                                            <Switch checked={responsableEstAdmin} onChange={setResponsableEstAdmin} style={responsableEstAdmin ? { background: PRIMARY } : undefined} />
                                            <Text style={{ marginLeft: 8 }}>{responsableEstAdmin ? "Oui" : "Renseigner un administrateur distinct"}</Text>
                                        </span>
                                    </div>
                                }
                            />
                            <PersonFields prefix="responsable" />
                        </div>

                        {/* ÉTAPE ADMINISTRATEUR (si distinct du responsable) */}
                        {!responsableEstAdmin && (
                            <div style={{ display: stepKey === "admin" ? "block" : "none" }}>
                                <Alert type="warning" showIcon style={{ marginBottom: 20, borderRadius: 8 }}
                                    message="L'administrateur disposera d'un compte distinct. Son e-mail doit différer de celui de la société." />
                                <PersonFields prefix="admin" />
                            </div>
                        )}

                        {/* ÉTAPE AGRÉMENTS */}
                        <div style={{ display: stepKey === "agrements" ? "block" : "none" }}>
                            <Form.Item label="Compagnies avec lesquelles vous avez une convention / un agrément" required>
                                <Select
                                    mode="multiple" allowClear showSearch optionFilterProp="label"
                                    placeholder="Sélectionnez une ou plusieurs compagnies"
                                    value={selectedCodes}
                                    onChange={handleSelectCompagnies}
                                    options={insurances.map((i) => ({ value: i.code, label: i.raisonSociale || i.nom || i.code }))}
                                />
                            </Form.Item>

                            {selectedCodes.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <Text strong style={{ color: PRIMARY }}>Documents d'agrément</Text>
                                    <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 12 }}>
                                        Joignez le document d'agrément (PDF/image) pour chaque compagnie sélectionnée.
                                    </Paragraph>
                                    {selectedCodes.map((code) => {
                                        const agr = agreements[code];
                                        return (
                                            <div key={code} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 14px", border: "1px solid #eee", borderRadius: 10, marginBottom: 10 }}>
                                                <span style={{ fontWeight: 500 }}>{agr?.company?.raisonSociale || agr?.company?.nom || code}</span>
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    {agr?.file
                                                        ? <Tag color="green" icon={<CheckCircleOutlined />}>{agr.file.name}</Tag>
                                                        : <Tag color="default">Aucun document</Tag>}
                                                    <Upload maxCount={1} showUploadList={false} beforeUpload={(file) => attachDoc(code, file)}>
                                                        <Button icon={<UploadOutlined />}>{agr?.file ? "Remplacer" : "Joindre"}</Button>
                                                    </Upload>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </Form>

                    <Divider style={{ margin: "12px 0 20px" }} />

                    {/* Navigation */}
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <Button onClick={prev} disabled={current === 0} icon={<ArrowLeftOutlined />}>Précédent</Button>
                        {current < steps.length - 1 ? (
                            <Button type="primary" onClick={next} style={{ background: PRIMARY }} icon={<ArrowRightOutlined />}>Suivant</Button>
                        ) : (
                            <Button type="primary" loading={loading} onClick={handleSubmit} style={{ background: PRIMARY }} icon={<CheckCircleOutlined />}>
                                S'enregistrer
                            </Button>
                        )}
                    </div>
                </Card>

                <div style={{ textAlign: "center", marginTop: 16 }}>
                    <Text style={{ fontSize: 12, color: "rgba(255,255,255,0.92)", textShadow: "0 1px 3px rgba(0,0,0,0.3)" }}>
                        Vous avez déjà un compte ? <a href="/connexion" style={{ color: "#fff", fontWeight: 600, textDecoration: "underline" }}>Se connecter</a>
                    </Text>
                </div>

                {/* Logos des compagnies membres (bas de page) */}
                {insurances?.length > 0 && (
                    <div style={{ marginTop: 32 }}>
                        <Text style={{
                            display: "block", textAlign: "center", color: "#fff", fontWeight: 700,
                            letterSpacing: 1, textTransform: "uppercase", marginBottom: 16, fontSize: 13,
                            textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                        }}>
                            Les compagnies membres
                        </Text>
                        <Row gutter={[12, 12]} justify="center" align="middle">
                            {insurances.map((ins, i) => (
                                <Col key={ins.code || i}>
                                    <div style={{
                                        background: "#fff", borderRadius: 12, padding: 12, width: 118, height: 68,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
                                    }}>
                                        {ins.logoDisplay ? (
                                            <img src={`data:image/png;base64,${ins.logoDisplay}`}
                                                alt={ins.raisonSociale || `Compagnie ${i + 1}`}
                                                style={{ maxWidth: "100%", maxHeight: "44px", objectFit: "contain" }} />
                                        ) : (
                                            <Text style={{ fontSize: 11, fontWeight: 600, color: PRIMARY, textAlign: "center" }}>
                                                {ins.raisonSociale || ins.code}
                                            </Text>
                                        )}
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RegisterV2;
